import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
  untracked,
} from '@angular/core';
import {
  Batch,
  BatchDepartureByIdRespWithSelected,
  BatchDocumentStatus,
  BatchMailingStatus,
  BatchStatusEnum,
} from '../../models';
import {
  faTimesCircle,
  faDownload,
  faPenSquare,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import { ActivatedRoute, Router } from '@angular/router';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons';
import { BatchesParseStorageService } from '../../services';
import { BatchDocumentsStoreService } from '$core/services';

@Component({
  selector: 'app-view-batch-card',
  templateUrl: './view-batch-card.component.html',
  styleUrls: ['./view-batch-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewBatchCardComponent {
  private readonly batchStorageService = inject(BatchesParseStorageService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly batchDocumentsStoreService = inject(
    BatchDocumentsStoreService
  );

  public readonly batchDeparture =
    input.required<BatchDepartureByIdRespWithSelected>();
  public readonly batch = input.required<Batch>();

  @Output() public changeCardSelected = new EventEmitter<{
    id: number;
    selected: boolean;
  }>();
  @Output() public deleteDeparture = new EventEmitter<number>();

  public isFullScreen$ = this.batchStorageService.isFullScreen$;

  public get isCompanyNameLong() {
    const personData =
      this.batchDeparture().recipient_contact_widget_data?.person;

    if (
      personData &&
      personData.type === 'legal' &&
      personData.organization_name.length > 29
    ) {
      return personData.organization_name;
    }

    return '';
  }

  public readonly faTrashAlt = faTrashAlt;
  public readonly faDownload = faDownload;
  public readonly faPenToSquare = faPenSquare;
  public readonly faCircleXmark = faTimesCircle;
  public readonly faCircleCheck = faCheckCircle;
  public readonly BatchStatusEnum = BatchStatusEnum;
  public readonly batchMailingStatus = BatchMailingStatus;
  public readonly BatchDocumentStatus = BatchDocumentStatus;

  protected readonly selectedLabelSize =
    this.batchStorageService.selectedLabelSize;

  protected readonly labelSizeUpdateRequestState =
    this.batchStorageService.labelSizeUpdateRequestState;

  protected readonly lastErroredDownloadDocumentType = signal<string | null>(
    null
  );

  /**
   * The state of the all labels request.
   */
  protected readonly loadingAllLabelsRequestState = computed(() => {
    if (this.batch()) {
      if (this.batch().documents?.status === BatchDocumentStatus.PROCESSING) {
        return BatchDocumentStatus.PROCESSING;
      }

      const storeItem = this.batchDocumentsStoreService.findSpecificStoreItem(
        { batchId: this.batch().id },
        true
      );
      return storeItem ? storeItem.documentStatus : 'idle';
    }
    return 'idle';
  });

  /**
   * The state of the label request.
   */
  protected readonly loadingLabelRequestState = computed(() => {
    if (this.batch() && this.batchDeparture) {
      if (
        this.batchDeparture().documents.some(
          (document) =>
            document.type === 'shipping_label' &&
            document.status === BatchDocumentStatus.PROCESSING
        )
      ) {
        return BatchDocumentStatus.PROCESSING;
      }

      const storeItem = this.batchDocumentsStoreService.findSpecificStoreItem(
        {
          batchId: this.batch().id,
          departureId: this.batchDeparture().id,
          documentType: 'shipping_label',
        },
        false
      );
      return storeItem ? storeItem.documentStatus : 'idle';
    }
    return 'idle';
  });

  /**
   * The state of the partial receipt request.
   */
  protected readonly loadingPartialReceiptRequestState = computed(() => {
    if (this.batch() && this.batchDeparture) {
      if (
        this.batchDeparture().documents.some(
          (document) =>
            document.type === 'partial_receipts' &&
            document.status === BatchDocumentStatus.PROCESSING
        )
      ) {
        return BatchDocumentStatus.PROCESSING;
      }

      const storeItem = this.batchDocumentsStoreService.findSpecificStoreItem(
        {
          batchId: this.batch().id,
          departureId: this.batchDeparture().id,
          documentType: 'partial_receipts',
        },
        false
      );
      return storeItem ? storeItem.documentStatus : 'idle';
    }
    return 'idle';
  });

  /**
   * Indicates whether the download documents buttons are disabled.
   */
  protected readonly isDownloadDocumentsDisabled = computed(
    () =>
      this.loadingAllLabelsRequestState() === BatchDocumentStatus.PROCESSING ||
      !this.selectedLabelSize() ||
      this.labelSizeUpdateRequestState().state === 'loading'
  );

  /**
   * Indicates whether the download label button is disabled.
   */
  protected readonly isDownloadLabelDisabled = computed(
    () =>
      this.isDownloadDocumentsDisabled() ||
      this.loadingLabelRequestState() === BatchDocumentStatus.PROCESSING
  );

  /**
   * Indicates whether the download partial receipt button is disabled.
   */
  protected readonly isDownloadPartialReceiptDisabled = computed(
    () =>
      this.isDownloadDocumentsDisabled() ||
      this.loadingPartialReceiptRequestState() ===
        BatchDocumentStatus.PROCESSING
  );

  /**
   * Indicates whether the download partial receipt button is available.
   */
  protected readonly isDownloadPartialReceiptAvailable = computed(
    () =>
      !!this.batchDeparture().generatable_document_types.find(
        (item) => item.type === 'partial_receipts'
      )
  );

  private readonly generateDepartureDocument$ =
    this.batchStorageService.generateDepartureDocument$;

  constructor() {
    effect(
      () => {
        const lastErroredDownloadDocumentType = untracked(() =>
          this.lastErroredDownloadDocumentType()
        );
        let typeToBeSet = null;

        if (
          this.loadingLabelRequestState() === BatchDocumentStatus.ERROR &&
          lastErroredDownloadDocumentType !== 'shipping_label'
        ) {
          typeToBeSet = 'shipping_label';
        }
        if (
          this.loadingPartialReceiptRequestState() ===
            BatchDocumentStatus.ERROR &&
          lastErroredDownloadDocumentType !== 'partial_receipts'
        ) {
          typeToBeSet = 'partial_receipts';
        }
        if (
          this.loadingLabelRequestState() !== BatchDocumentStatus.ERROR &&
          this.loadingPartialReceiptRequestState() !==
            BatchDocumentStatus.ERROR &&
          lastErroredDownloadDocumentType !== null
        ) {
          typeToBeSet = null;
        }

        this.lastErroredDownloadDocumentType.set(typeToBeSet);
      },
      { allowSignalWrites: true }
    );
  }

  public changeSelected(selected: boolean): void {
    this.changeCardSelected.emit({ id: this.batchDeparture().id, selected });
  }

  public navigateToEditDeparturePage(): void {
    const id = this.activatedRoute.snapshot.params['id'];

    this.router.navigate([`personal/batch/batches/${id}/edit`], {
      queryParams: {
        departureId: this.batchDeparture().id,
      },
    });
  }

  public redirectToBatchDepartureCard(): void {
    const id = this.activatedRoute.snapshot.params['id'];

    if (
      this.batch()?.is_partial_receipt &&
      this.batch()?.status === BatchStatusEnum.UNCOMMITED
    ) {
      this.router.navigate(
        [`personal/batch/batches/${id}/view-departure-attachments`],
        {
          queryParams: {
            departureId: this.batchDeparture().id,
          },
        }
      );
    } else {
      this.router.navigate([`personal/batch/batches/${id}/departure-card`], {
        queryParams: {
          departureId: this.batchDeparture().id,
        },
      });
    }
  }

  public showRecipientName(
    batchDeparture: BatchDepartureByIdRespWithSelected
  ): string {
    switch (batchDeparture.recipient_contact_widget_data.person.type) {
      case 'legal':
      case 'individual_entrepreneur':
        return batchDeparture.recipient_contact_widget_data.person
          .organization_name;
      case 'individual':
        return `${
          batchDeparture.recipient_contact_widget_data.person.last_name
        } ${
          batchDeparture.recipient_contact_widget_data.person.first_name[0]
        }. ${
          batchDeparture.recipient_contact_widget_data.person.second_name
            ? batchDeparture.recipient_contact_widget_data.person
                .second_name[0] + '.'
            : ''
        }`;
      default:
        return '-';
    }
  }

  protected downloadDepartureLabel(): void {
    const document = this.batchDeparture().documents.find(
      (document) => document.type === 'shipping_label'
    );
    const documentId = document?.id ?? null;
    const documentStatus = document?.status ?? null;

    this.generateDepartureDocument$.next({
      departureId: this.batchDeparture().id,
      documentType: 'shipping_label',
      s10Code: this.batchDeparture().s10code,
      documentId: documentId,
      documentStatus: documentStatus,
    });
  }

  protected downloadDeparturePartialReceipt(): void {
    const document = this.batchDeparture().documents.find(
      (document) => document.type === 'partial_receipts'
    );
    const documentStatus = document?.status ?? null;
    const documentId = document?.id ?? null;

    this.generateDepartureDocument$.next({
      departureId: this.batchDeparture().id,
      documentType: 'partial_receipts',
      s10Code: this.batchDeparture().s10code,
      documentId: documentId,
      documentStatus: documentStatus,
    });
  }
}
